import type {
  PlacementDetailsAllocatedJobShift,
  PlacementDetailsAppliedModification,
} from 'src/store/placementDetailsPageSlice/types';

// **** Types **** //

export interface ModificationListItem {
  modificationText: string;
  taskNames: string[];
}

// **** Functions **** //

/**
 * @see allocatedJobShiftsToModificationDisplayList
 *
 * Get an array of task names sorted and de-duplicated.
 */
const getTaskNamesArray = (appliedModifications: PlacementDetailsAppliedModification[]) => {
  const dedupedNames = new Set(appliedModifications.map((am) => am.taskName));
  return [...dedupedNames].sort((a, b) => a.localeCompare(b));
};

/**
 * Convert a single or "list of" AllocatedJobShifts to a user readable list of modifications.
 */
export const allocatedJobShiftsToModificationDisplayList = (
  allocatedJobShifts: PlacementDetailsAllocatedJobShift | PlacementDetailsAllocatedJobShift[],
): ModificationListItem[] => {
  // Get the list of applied modifications
  const jobsShifts = Array.isArray(allocatedJobShifts) ? allocatedJobShifts : [allocatedJobShifts];
  const allAppliedMods = jobsShifts.flatMap((ajs) => ajs.appliedModifications);
  // Group the modifications by name and free form text
  const groupedMods = new Map<string, PlacementDetailsAppliedModification[]>();
  allAppliedMods.forEach((appliedMod) => {
    const { modificationName, freeFormText } = appliedMod;
    const fft = freeFormText ? ': ' + freeFormText : '';
    const key = `${modificationName}${fft}`;
    const group = groupedMods.get(key) ?? [];
    group.push(appliedMod);
    groupedMods.set(key, group);
  });
  // Setup the result
  return Array.from(groupedMods.entries())
    .map(([key, appliedMods]) => ({
      modificationText: key,
      taskNames: getTaskNamesArray(appliedMods),
    }))
    .sort((a, b) => a.modificationText.localeCompare(b.modificationText));
};
